// SPDX-License-Identifier: AGPL-3.0-or-later

export const MIN_ZOOM_SCALE = 1;
export const DEFAULT_ZOOM_SCALE = 2.5;
export const MAX_ZOOM_SCALE = 5;
export const ZOOM_STEP = 1.5;
export const ZOOM_STATE_EPSILON = 0.015;
export const TAP_MOVE_THRESHOLD = 20;
/** A mouse click that moves further than this is a drag, not a click. */
export const MOUSE_CLICK_MOVE_THRESHOLD = 5;
/** However large the picture, zooming in may go this far past fit. */
export const MAX_ACTUAL_SIZE_ZOOM_SCALE = 20;
/** A click or double tap zooms to the picture's real pixels, kept within this range. */
export const AUTO_ZOOM_MIN_SCALE = 2;
export const AUTO_ZOOM_MAX_SCALE = 4;
/** One notch of a mouse wheel. */
export const WHEEL_NOTCH_ZOOM_STEP = 1.25;
/** Trackpad pinches arrive as many small ctrl+wheel deltas. */
export const PINCH_WHEEL_ZOOM_SPEED = 0.01;
export const SWIPE_DOWN_DISMISS_MIN_DISTANCE = 48;
export const SWIPE_DOWN_DISMISS_MIN_VELOCITY = 0.75;
export const SWIPE_DOWN_DISMISS_VERTICAL_BIAS = 1.2;
export const SWIPE_SIDE_MIN_DISTANCE = 40;
export const SWIPE_SIDE_MIN_VELOCITY = 0.5;
export const SWIPE_SIDE_HORIZONTAL_BIAS = 1.2;

export interface Point {
	x: number;
	y: number;
}

export interface PanZoomMetrics {
	viewportWidth: number;
	viewportHeight: number;
	contentWidth: number;
	contentHeight: number;
}

export function isSwipeDownIntent(dx: number, dy: number): boolean {
	return dy >= TAP_MOVE_THRESHOLD && dy >= Math.abs(dx) * SWIPE_DOWN_DISMISS_VERTICAL_BIAS;
}

export function shouldDismissAfterSwipeDown({
	dx,
	dy,
	durationMs,
	viewportHeight,
}: {
	dx: number;
	dy: number;
	durationMs: number;
	viewportHeight: number;
}): boolean {
	if (!isSwipeDownIntent(dx, dy)) return false;
	const distanceThreshold = Math.min(160, Math.max(96, viewportHeight * 0.18));
	const velocity = durationMs > 0 ? dy / durationMs : 0;
	return (
		dy >= distanceThreshold || (dy >= SWIPE_DOWN_DISMISS_MIN_DISTANCE && velocity >= SWIPE_DOWN_DISMISS_MIN_VELOCITY)
	);
}

export function isSwipeSideIntent(dx: number, dy: number): boolean {
	return Math.abs(dx) >= TAP_MOVE_THRESHOLD && Math.abs(dx) >= Math.abs(dy) * SWIPE_SIDE_HORIZONTAL_BIAS;
}

/** -1 when a sideways swipe should step to the previous item, 1 for the next one, 0 to stay put. */
export function swipeSideStep({
	dx,
	dy,
	durationMs,
	viewportWidth,
}: {
	dx: number;
	dy: number;
	durationMs: number;
	viewportWidth: number;
}): -1 | 0 | 1 {
	if (!isSwipeSideIntent(dx, dy)) return 0;
	const distance = Math.abs(dx);
	const distanceThreshold = Math.min(140, Math.max(64, viewportWidth * 0.22));
	const velocity = durationMs > 0 ? distance / durationMs : 0;
	const far =
		distance >= distanceThreshold || (distance >= SWIPE_SIDE_MIN_DISTANCE && velocity >= SWIPE_SIDE_MIN_VELOCITY);
	if (!far) return 0;
	return dx < 0 ? 1 : -1;
}

/**
 * How far the surface may zoom: at least the configured maximum, and enough to see a large picture at
 * twice its real pixels. With `pixelsPerPicturePixel` (phones pass the screen's pixel ratio) that is
 * twice its real pixels on the screen rather than in CSS pixels: iOS has to repaint the whole picture at
 * every zoom level, and a 20x repaint of a large screenshot can leave a flat light box in its place.
 */
export function getEffectiveMaxScale(
	maxScale: number,
	actualSizeScale: number | null,
	pixelsPerPicturePixel = 1,
): number {
	if (actualSizeScale == null || !Number.isFinite(actualSizeScale)) return maxScale;
	const density = Number.isFinite(pixelsPerPicturePixel) && pixelsPerPicturePixel > 1 ? pixelsPerPicturePixel : 1;
	return Math.max(maxScale, Math.min(MAX_ACTUAL_SIZE_ZOOM_SCALE, (actualSizeScale * 2) / density));
}

/** Where a click or double tap zooms to: the picture's real pixels when it is shown smaller, within reason. */
export function getAutoZoomScale(actualSizeScale: number | null, fallback: number): number {
	if (actualSizeScale == null || !Number.isFinite(actualSizeScale) || actualSizeScale <= 1 + ZOOM_STATE_EPSILON) {
		return fallback;
	}
	return clamp(actualSizeScale, AUTO_ZOOM_MIN_SCALE, AUTO_ZOOM_MAX_SCALE);
}

interface WheelLike {
	deltaX: number;
	deltaY: number;
	deltaMode: number;
	wheelDeltaY?: number;
}

/**
 * A mouse wheel moves in notches; a trackpad scrolls smoothly. Chromium and Safari report a trackpad's
 * wheelDeltaY as exactly -3 × deltaY, Firefox reports a mouse in lines.
 */
export function isNotchedWheel(event: WheelLike): boolean {
	if (event.deltaMode !== 0) return true;
	if (event.deltaY === 0) return false;
	const wheelDeltaY = event.wheelDeltaY;
	if (typeof wheelDeltaY === 'number' && wheelDeltaY !== 0) {
		return Math.abs(wheelDeltaY + 3 * event.deltaY) > 1 && Math.abs(wheelDeltaY) % 120 === 0;
	}
	return event.deltaX === 0 && Math.abs(event.deltaY) >= 50;
}

/** The scale factor for one wheel event: a fixed step per mouse notch, proportional for a pinch. */
export function getWheelZoomFactor(event: WheelLike, notched: boolean, viewportHeight: number): number {
	if (event.deltaY === 0) return 1;
	if (notched) return event.deltaY < 0 ? WHEEL_NOTCH_ZOOM_STEP : 1 / WHEEL_NOTCH_ZOOM_STEP;
	const pixels =
		event.deltaMode === 1 ? event.deltaY * 16 : event.deltaMode === 2 ? event.deltaY * viewportHeight : event.deltaY;
	return clamp(Math.exp(-pixels * PINCH_WHEEL_ZOOM_SPEED), 0.5, 2);
}

function clamp(value: number, min: number, max: number): number {
	const clamped = Math.min(max, Math.max(min, value));
	return Object.is(clamped, -0) ? 0 : clamped;
}

export function clampScale(value: number, minScale = MIN_ZOOM_SCALE, maxScale = MAX_ZOOM_SCALE): number {
	return clamp(value, minScale, maxScale);
}

export function isDefaultTransform({
	scale,
	x,
	y,
	minScale = MIN_ZOOM_SCALE,
	epsilon = ZOOM_STATE_EPSILON,
}: {
	scale: number;
	x: number;
	y: number;
	minScale?: number;
	epsilon?: number;
}): boolean {
	return Math.abs(scale - minScale) <= epsilon && Math.abs(x) <= epsilon && Math.abs(y) <= epsilon;
}

export function getDistance(a: Point, b: Point): number {
	return Math.hypot(a.x - b.x, a.y - b.y);
}

export function getCentroid(a: Point, b: Point): Point {
	return {
		x: (a.x + b.x) / 2,
		y: (a.y + b.y) / 2,
	};
}

export function getViewportPoint(clientX: number, clientY: number, viewport: DOMRect): Point {
	return {
		x: clientX - viewport.left - viewport.width / 2,
		y: clientY - viewport.top - viewport.height / 2,
	};
}

export function clampPanForScale(point: Point, scale: number, metrics: PanZoomMetrics): Point {
	if (scale <= MIN_ZOOM_SCALE + ZOOM_STATE_EPSILON) {
		return {x: 0, y: 0};
	}
	const maxX = Math.max(0, (metrics.contentWidth * scale - metrics.viewportWidth) / 2);
	const maxY = Math.max(0, (metrics.contentHeight * scale - metrics.viewportHeight) / 2);
	return {
		x: clamp(point.x, -maxX, maxX),
		y: clamp(point.y, -maxY, maxY),
	};
}

export function getAnchoredZoomPoint({
	origin,
	current,
	currentScale,
	nextScale,
	metrics,
}: {
	origin: Point;
	current: Point;
	currentScale: number;
	nextScale: number;
	metrics: PanZoomMetrics;
}): Point {
	if (nextScale <= MIN_ZOOM_SCALE + ZOOM_STATE_EPSILON) {
		return {x: 0, y: 0};
	}
	const scaleRatio = nextScale / Math.max(currentScale, MIN_ZOOM_SCALE);
	return clampPanForScale(
		{
			x: origin.x - (origin.x - current.x) * scaleRatio,
			y: origin.y - (origin.y - current.y) * scaleRatio,
		},
		nextScale,
		metrics,
	);
}
